<script setup>
/*
 * Headless UI's `Popover` already powers the old navigation, but it has no
 * hover-intent handling — the old bar works around that by dispatching a click
 * from `@mouseover`. The open/close timing is the whole point of this
 * component, so the disclosure pattern is built out by hand instead.
 */
import { ref, useId, useTemplateRef, watch } from 'vue';
import { onClickOutside, onKeyStroke, useTimeoutFn } from '@vueuse/core';
import MainNavigationFlyout from './MainNavigationFlyout.vue';
import NavigationLabel from './NavigationLabel.vue';
import NavigationLink from './NavigationLink.vue';
import { focusRing } from './navigationClasses.js';

const props = defineProps({
    items: {
        type: Array,
        default: () => [],
    },
    openDelay: {
        type: Number,
        default: 80,
    },
    closeDelay: {
        type: Number,
        default: 200,
    },
});

/** Mirrors `w-70` on the flyout, plus the gap it should keep to the viewport edge. */
const flyoutWidth = 280;
const viewportMargin = 16;

const navigation = useTemplateRef('navigation');
const openIndex = ref(null);
const alignment = ref('center');
const idPrefix = useId();

const alignmentClasses = {
    start: 'left-0',
    center: 'left-1/2 -translate-x-1/2',
    end: 'right-0',
};

/** Trigger elements by index — read imperatively, so no reactivity needed. */
const triggers = new Map();

let requestedIndex = null;

const { start: scheduleOpen, stop: cancelOpen } = useTimeoutFn(() => {
    openIndex.value = requestedIndex;
}, () => props.openDelay, { immediate: false });

const { start: scheduleClose, stop: cancelClose } = useTimeoutFn(() => {
    openIndex.value = null;
}, () => props.closeDelay, { immediate: false });

const hasFlyout = item => Boolean(item.children?.length);

/*
 * The position in the bar identifies an entry, not its permalink: the real
 * navigation keeps group headings that carry children but no `permalink`, and
 * `null` is what a closed bar already means.
 */
const panelId = index => `${idPrefix}-flyout-${index}`;
const triggerId = index => `${idPrefix}-trigger-${index}`;

const setTriggerReference = (element, index) => {
    if (element) {
        triggers.set(index, element);
    } else {
        triggers.delete(index);
    }
};

function close() {
    cancelOpen();
    cancelClose();
    openIndex.value = null;
}

function toggle(index) {
    cancelOpen();
    cancelClose();
    openIndex.value = openIndex.value === index ? null : index;
}

/**
 * Opens after a short intent delay. While another flyout is already open the
 * switch is immediate, so moving along the bar does not feel sluggish.
 */
function openWithIntent(index) {
    cancelClose();

    if (openIndex.value !== null) {
        cancelOpen();
        openIndex.value = index;

        return;
    }

    requestedIndex = index;
    scheduleOpen();
}

function handlePointerEnter(event, item, index) {
    if (event.pointerType !== 'mouse') {
        return;
    }

    if (hasFlyout(item)) {
        openWithIntent(index);

        return;
    }

    cancelOpen();
    scheduleClose();
}

function handlePointerLeave(event) {
    if (event.pointerType !== 'mouse') {
        return;
    }

    cancelOpen();
    scheduleClose();
}

/**
 * Keyboard users tab straight out of a flyout onto the next entry, which would
 * otherwise leave the previous panel open behind them. Focus does not open
 * anything — that stays with click, Enter and Space.
 */
function handleEntryFocus(index) {
    if (openIndex.value === index) {
        return;
    }

    close();
}

function handleFocusOut(event) {
    if (event.relatedTarget && navigation.value?.contains(event.relatedTarget)) {
        return;
    }

    close();
}

/**
 * The panel is centred under its trigger, which pushes it off screen for the
 * outermost entries on narrow viewports — those fall back to the matching edge.
 * Measured on open: an open flyout closes on the next interaction anyway, so
 * there is nothing to re-measure on resize. Only the trigger is measured, and
 * it is already in the DOM — so this runs before the panel renders, which gets
 * the alignment right on the first frame.
 */
watch(openIndex, index => {
    if (index === null) {
        return;
    }

    const trigger = triggers.get(index);

    if (!trigger) {
        return;
    }

    const { left, right } = trigger.getBoundingClientRect();
    const centre = (left + right) / 2;
    const half = flyoutWidth / 2;

    if (centre - half < viewportMargin) {
        alignment.value = 'start';
    } else if (centre + half > globalThis.innerWidth - viewportMargin) {
        alignment.value = 'end';
    } else {
        alignment.value = 'center';
    }
});

onKeyStroke('Escape', () => {
    if (openIndex.value === null) {
        return;
    }

    const focusedIndex = openIndex.value;
    // Hover opened the flyout while the focus sat elsewhere — leave it there.
    const shouldRestoreFocus = navigation.value?.contains(document.activeElement);

    close();

    if (shouldRestoreFocus) {
        triggers.get(focusedIndex)?.focus();
    }
});

onClickOutside(navigation, close);

const itemClasses = `text-base rounded-md px-3 py-2 decoration-1 underline-offset-4 hover:underline focus-visible:underline transition-colors ${focusRing}`;

/*
 * Pointing at an entry and being inside its section are two different things,
 * so they get two different treatments and stack instead of replacing each
 * other: the underline follows the pointer and the focus (see `itemClasses`),
 * and an open flyout holds it while the pointer moves down into the panel. The
 * weight marks the section the current page belongs to, matching how the
 * flyout marks its own entries.
 */
const stateClasses = (item, index) => {
    const isOpen = openIndex.value === index;
    const isCurrentSection = item.is_parent || item.is_current;

    return [
        isOpen || isCurrentSection
            ? 'text-deep-teal-900 purple-theme:text-purple-900'
            : 'text-deep-teal-700 purple-theme:text-purple-700',
        { underline: isOpen, 'font-semibold': isCurrentSection },
    ];
};
</script>

<template>
    <nav
        ref="navigation"
        @focusout="handleFocusOut"
    >
        <ul class="flex items-center">
            <li
                v-for="(item, index) in items"
                :key="index"
                class="relative"
                @pointerenter="handlePointerEnter($event, item, index)"
                @pointerleave="handlePointerLeave($event)"
            >
                <template v-if="hasFlyout(item)">
                    <button
                        :id="triggerId(index)"
                        :ref="element => setTriggerReference(element, index)"
                        type="button"
                        :class="[itemClasses, stateClasses(item, index)]"
                        :aria-expanded="openIndex === index"
                        :aria-controls="panelId(index)"
                        @focus="handleEntryFocus(index)"
                        @click="toggle(index)"
                    >
                        <NavigationLabel :title="item.title" />
                    </button>

                    <transition
                        enter-active-class="transition ease-out duration-200 motion-reduce:transition-none"
                        enter-from-class="opacity-0 -translate-y-1"
                        enter-to-class="opacity-100 translate-y-0"
                        leave-active-class="transition ease-in duration-150 motion-reduce:transition-none"
                        leave-from-class="opacity-100 translate-y-0"
                        leave-to-class="opacity-0 -translate-y-1"
                    >
                        <div
                            v-show="openIndex === index"
                            class="absolute top-full z-20 pt-2"
                            :class="alignmentClasses[alignment]"
                        >
                            <MainNavigationFlyout
                                :id="panelId(index)"
                                :item
                                :aria-labelledby="triggerId(index)"
                            />
                        </div>
                    </transition>
                </template>

                <!-- Without children there is nothing to disclose, so link straight to the page. -->
                <NavigationLink
                    v-else
                    :item
                    class="block"
                    :class="[itemClasses, stateClasses(item, index)]"
                    @focus="handleEntryFocus(index)"
                >
                    <NavigationLabel :title="item.title" />
                </NavigationLink>
            </li>
        </ul>
    </nav>
</template>
